import React, { useCallback, useState } from 'react';

import styled from 'styled-components';

import { ISanityImage } from '@rbi-ctg/menu';
import { ResponsiveCarousel } from 'components/responsive-carousel';
import useMediaQuery from 'hooks/use-media-query';

import { TastySwapCard } from './tasty-swap-card';
import { TastySwapModal } from './tasty-swap-modal';
import { ISwap, ITastySwaps } from './types';

const CONTAINER_BG_COLOR = '#e7e0dc';

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 2rem;
  background-color: transparent;

  ${Styles.desktop`
    background-color: ${CONTAINER_BG_COLOR};
  `};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  padding: 1rem 0;
`;

const Title = styled.h3`
  margin: 0;
  margin-bottom: 0.4rem;

  text-transform: ${Styles.textTransform.headlines};
  font-family: ${Styles.fontFamily.base};
  font-size: 1.1rem;
  letter-spacing: 1px;

  ${Styles.desktop`
    font-size: 1.4rem;
  `}
`;

const Subtitle = styled.h4`
  max-width: 208px;
  margin: 0;
  line-height: 1rem;

  text-transform: ${Styles.textTransform.headlines};
  font-family: ${Styles.fontFamily.base};
  font-size: 0.75rem;
  font-weight: lighter;
  text-align: center;

  ${Styles.desktop`
    max-width: none;
    margin-bottom: 40px;
    font-size: 0.9rem;
  `}
`;

const MobileCollectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px 0;

  & > * + * {
    margin-top: 8px;
  }

  ${Styles.desktop`
    padding-top: 40px;
  `}
`;

interface IMobileCollection {
  swaps: ISwap[];
  onClick: (swap: ISwap) => void;
  tastySwapCardBackgroundImage?: ISanityImage;
}

const MobileCollection: React.FC<IMobileCollection> = ({
  swaps,
  onClick,
  tastySwapCardBackgroundImage,
}) => {
  return (
    <MobileCollectionContainer>
      {swaps.map(swap => (
        <TastySwapCard
          key={`${swap.selectedProduct._id}-${swap.suggestedProduct._id}`}
          backgroundImage={tastySwapCardBackgroundImage}
          onClick={onClick}
          swap={swap}
        />
      ))}
    </MobileCollectionContainer>
  );
};

interface ITastySwapsCollection {
  tastySwaps: ITastySwaps;
  tastySwapCardBackgroundImage?: ISanityImage;
}

export const TastySwapsCollection: React.FC<ITastySwapsCollection> = ({
  tastySwaps,
  tastySwapCardBackgroundImage,
}) => {
  const isMobile = useMediaQuery('mobile');
  const [selectedSwap, setSelectedSwap] = useState<ISwap | null>(null);

  const onSelectTastySwap = useCallback((swap: ISwap) => {
    setSelectedSwap(swap);
  }, []);
  const onDismissModal = useCallback(() => setSelectedSwap(null), []);

  return (
    <>
      <Container>
        <Content>
          <Title>{tastySwaps.mainTitle}</Title>
          <Subtitle>{tastySwaps.mainSubtitle}</Subtitle>
          {isMobile ? (
            <MobileCollection
              swaps={tastySwaps.swaps}
              tastySwapCardBackgroundImage={tastySwapCardBackgroundImage}
              onClick={onSelectTastySwap}
            />
          ) : (
            <ResponsiveCarousel bgColor={CONTAINER_BG_COLOR}>
              {tastySwaps.swaps.map(swap => (
                <TastySwapCard
                  key={`${swap.selectedProduct._id}-${swap.suggestedProduct._id}`}
                  backgroundImage={tastySwapCardBackgroundImage}
                  onClick={onSelectTastySwap}
                  swap={swap}
                />
              ))}
            </ResponsiveCarousel>
          )}
        </Content>
      </Container>
      {selectedSwap && (
        <TastySwapModal
          title={tastySwaps.modalTitle}
          subtitle={tastySwaps.modalSubtitle}
          swap={selectedSwap}
          backgroundImage={tastySwapCardBackgroundImage}
          onDismiss={onDismissModal}
        />
      )}
    </>
  );
};
